import React, { useContext, useEffect, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button, Grid, IconButton, Typography, makeStyles } from "@material-ui/core";
import Collapse from "@material-ui/core/Collapse";
import { FileCopyOutlined } from "@material-ui/icons";

import { Search } from "../../components/FormElements";
import { SectionGridContainer, SectionGridLayout } from "../../components/Layout";
import { RiderContext } from "../../context";
import { useTestIds } from "../../hooks/useTestIds";
import Table from "../../components/Layout/Table";

const useStyles = makeStyles(() => ({
  idCell: {
    display: "flex",
  },
  idValue: {
    display: "block",
    margin: "auto 1em auto 0",
    width: "8em",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  padding: {
    padding: "0.2em 1em",
  },
}));

const RiderSearch = () => {
  const DEFAULT_TABLE_ROWS = 50;
  const classes = useStyles();
  const testIds = useTestIds("rider-search");

  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_TABLE_ROWS);
  const { searchRiders, riders, riderCount } = useContext(RiderContext);
  const { t } = useTranslation();

  useEffect(() => {
    const waitForRiders = async () => {
      setLoading(true);
      await searchRiders(phoneNumber, rowsPerPage, pageNumber + 1);
      setLoading(false);
    };
    waitForRiders();
    return () => {
      searchRiders(null);
    };
  }, [pageNumber, rowsPerPage, phoneNumber]);

  const tableColumns = [
    {
      canonicalName: "id",
      name: t("Riders.RiderSearch.TableHeader.RiderId"),
      tableCellProps: { className: classes.padding },
      renderCellContent: (row) => (
        <div className={classes.idCell}>
          <span className={classes.idValue}>{row.id}</span>
          <CopyToClipboard text={row.id}>
            <IconButton aria-label="Copy">
              <FileCopyOutlined />
            </IconButton>
          </CopyToClipboard>
        </div>
      ),
    },
    {
      canonicalName: "phone_number",
      name: t("Riders.RiderSearch.TableHeader.PhoneNumber"),
      tableCellProps: { className: classes.padding },
    },
    {
      canonicalName: "email",
      name: t("Riders.RiderSearch.TableHeader.Email"),
      tableCellProps: { className: classes.padding },
    },
    {
      canonicalName: "view",
      name: "",
      tableCellProps: { align: "center", padding: "none", className: classes.padding },
      renderCellContent: (row) => (
        <Link to={`/riders/${row.id}`}>
          <Button>{t("Riders.RiderSearch.View")}</Button>
        </Link>
      ),
    },
  ];

  return (
    <SectionGridLayout>
      <SectionGridContainer spaceContents testId={testIds.wrapper}>
        <Grid item xs={12}>
          <Typography data-testid={testIds.title} variant="h5">
            {t("Riders.RiderSearch.Title")}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Search
            onChange={(text) => setPhoneNumber(text.replace(/\D/g, ""))}
            debounce={500}
            placeholder={t("Riders.RiderSearch.SearchPhoneNumber")}
            loading={loading}
            inputProps={{ "data-testid": testIds.searchBar }}
          />
        </Grid>
      </SectionGridContainer>
      <Collapse in={riders.length > 0} timeout={1000}>
        <SectionGridContainer style={{ maxHeight: "100%" }}>
          <Table
            muiTableProps={{ stickyHeader: true }}
            rows={riders}
            columns={tableColumns}
            paginationProps={{
              rowsPerPage,
              rowsPerPageOptions: [5, 10, 50, 100],
              pageNumber,
              totalRowsCount: riderCount,
              setPageNumber,
              setRowsPerPage,
            }}
          />
        </SectionGridContainer>
      </Collapse>
    </SectionGridLayout>
  );
};

export default RiderSearch;
